import React, { ReactElement, useMemo, useRef } from 'react';
import MovieInfo from '../../components/MovieInfo/MovieInfo';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import {
	movieContentsSelector,
	movieSessionsErrorSelector,
	movieSessionsSelector,
} from '../../redux/slices/movies/selectors';
import MovieDetail from '../MovieDetail/MovieDetail';
import { useRouter } from 'next/router';
import { MOVIE_DETAIL, MOVIE_REVIEWS, SHOW_LISTING } from '../../constants/moviesConst';
import MDPTabs from '../../components/MDPTabs/MDPTabs';
import { useState } from 'react';
import { useEffect } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { useCallback } from 'react';
import { City } from '../../types/city';
import { setUserCityName, checkDateForSetAlert, checkH5ScreenName } from '../../services/coreUtil';
//import Overlay from '../../components/Overlay/Overlay';
//import MovieCitySelector from '../MovieCitySelector/MovieCitySelector';
//import LanguageSelectionDialog from '../../components/LanguageSelectionDialog/LanguageSelectionDialog';
import dynamic from 'next/dynamic';
const LanguageSelectionDialog = dynamic(
	() => import('../../components/LanguageSelectionDialog/LanguageSelectionDialog'),
);
const Overlay = dynamic(() => import('../../components/Overlay/Overlay'));
const MovieCitySelector = dynamic(() => import('../MovieCitySelector/MovieCitySelector'));
import { gtmUtils } from '../../services/GTMUtils';
import { getUserFlow } from '../../redux/slices/user/selectors';
import { getMovieDetailUrl } from '../../services/urlCreator';
const MovieCriticReviewWrapper = dynamic(() => import('../MovieReviewWrapper/MovieReviewWrapper'));
const MovieShowTimings = dynamic(() => import('../MovieShowTimings/MovieShowTimings'));
interface Props {
	contentId: string;
	cityName: string;
	cityLabel: string;
	cinemaIds?: string;
}

const gaPayload = {
	event_category: 'Movie Tickets - Showtime',
	screenName: checkH5ScreenName('Movie Tickets - Showtime'),
};

function MDPContainer({ contentId, cityName, cityLabel, cinemaIds = '' }: Props): ReactElement {
	const moviecontentData = useAppSelector(movieContentsSelector);
	const movieSessionsError = useAppSelector(movieSessionsErrorSelector);
	const dispatch = useAppDispatch();
	const [selectedTab, setSelectedTab] = useState(MOVIE_DETAIL);
	const [searchInputText, setSearchInputText] = useState('');
	const router = useRouter();
	let movieDetails = moviecontentData[contentId];
	const { query } = router;
	const { tab = '', frmtid = '', fromdate = '', flow: flowQuery = '' } = query;
	const [showLanguageSelector, setShowLanguageSelector] = useState(false);
	const cinemaIdRef = useRef(cinemaIds);
	const [showAlert, setShowAlert] = useState(false);
	const movieSessionsData = useAppSelector(movieSessionsSelector);
	const flow = useAppSelector(getUserFlow) || flowQuery;
	const movieSessions = useMemo(() => {
		const keyInStore = ((frmtid as string) + fromdate + (cinemaIds || flow || '')) as string;
		return movieSessionsData[keyInStore] || {};
	}, [frmtid, fromdate, movieSessionsData, cinemaIds, flow]);
	const movieSessionDetails = movieSessions?.meta?.movies?.find(
		(e: any) => e.frmtId.toLowerCase() === frmtid,
	);
	movieDetails = {
		...movieDetails,
		...movieSessionDetails,
	};

	const closeLanguageSelector = () => {
		setShowLanguageSelector(false);
		router.replace(`${getMovieDetailUrl(movieDetails?.name, contentId)}?tab=about`);
	};

	useEffect(() => {
		if (
			tab !== 'about' &&
			tab !== 'reviews' &&
			!isEmpty(movieDetails.languageFormatGroups) &&
			!frmtid &&
			cityName
		) {
			if (movieDetails.languageFormatGroups.length > 1) return setShowLanguageSelector(true);
			if (movieDetails.languageFormatGroups.length === 1) {
				router.replace(
					`${getMovieDetailUrl(movieDetails?.name, contentId)}?frmtid=${
						movieDetails.languageFormatGroups[0].fmtGrpId
					}`,
				);
			}
		} else {
			setShowLanguageSelector(false);
		}
	}, [cityName, frmtid, tab]);

	const toggleTabs = useCallback(() => {
		if (
			tab !== 'about' &&
			tab !== 'reviews' &&
			!isEmpty(movieDetails.languageFormatGroups) &&
			frmtid &&
			!movieSessionsError
		)
			setSelectedTab(SHOW_LISTING);
		else if (tab === 'reviews') setSelectedTab(MOVIE_REVIEWS);
		else if (!frmtid || movieSessionsError || tab === 'about') {
			setSelectedTab(MOVIE_DETAIL);
		}
	}, [frmtid, movieDetails?.languageFormatGroups, movieSessionsError, tab]);

	useEffect(() => {
		toggleTabs();
	}, [tab, toggleTabs]);

	useEffect(() => {
		if (get(movieDetails, 'releaseDate', '')) {
			const res = checkDateForSetAlert(movieDetails.releaseDate);
			if (res) {
				setShowAlert(true);
			} else {
				setShowAlert(false);
			}
		}
	}, [movieDetails]);

	const handleCityCase = async (city: City) => {
		setUserCityName(city.value, city.name);
		window.location.reload();
	};

	const mdpTabClickEvent = (eventAction = 'About tab Clicked') => {
		gtmUtils.gtmMoviePageEvents({
			...gaPayload,
			event_action: eventAction,
			event_label5: cityName,
			event_label6: movieDetails?.name,
			event_label7: String(contentId),
			event_label11:
				movieDetails?.languageFormatGroups.find((e) => e.fmtGrpId === frmtid)?.lang || null,
		});
	};

	useEffect(() => {
		if (!isEmpty(movieDetails) && !!frmtid) {
			const languageFormatGroups = movieDetails?.languageFormatGroups;
			const isFrmtIdValid = languageFormatGroups?.find((ele: any) => ele.fmtGrpId === frmtid);
			if (!isFrmtIdValid) {
				const query: any = {};
				if (languageFormatGroups?.length === 1) {
					query.frmtid = languageFormatGroups[0].fmtGrpId;
				}
				router.replace(
					{ pathname: getMovieDetailUrl(movieDetails.name, contentId), query },
					undefined,
					{ scroll: false },
				);
			}
		}
	}, [movieDetails, frmtid, router, contentId]);

	return (
		<>
			<div>
				{!cityName && (
					<Overlay fullHeight>
						<MovieCitySelector onCityClick={handleCityCase} />
					</Overlay>
				)}
				<MovieInfo
					movieDetails={movieDetails}
					onSearch={(e) => setSearchInputText(e.target.value)}
					resetSearch={() => setSearchInputText('')}
					searchInputValue={searchInputText}
					showSearch={selectedTab === SHOW_LISTING}
					cityName={cityName}
					frmtid={frmtid}
					contentId={contentId}
					languageSelected={movieDetails.languageFormatGroups?.find((e) => e.fmtGrpId === frmtid)}
					path={router.asPath}
				/>
				<div>
					<MDPTabs
						movieDetails={movieDetails}
						selectedTab={selectedTab}
						contentId={contentId}
						clickEvent={mdpTabClickEvent}
					/>
					{/* <div className={s.bookLinks}>
                                              <MovieSentiment movie={movieDetail} metricInfo={{}} isUpcoming={false} />
                                          </div> */}
				</div>
				{tab !== 'about' && tab !== 'reviews' && frmtid && cityName && !movieSessionsError && (
					<MovieShowTimings
						contentId={contentId}
						searchInputValue={searchInputText}
						cinemaIds={cinemaIdRef.current}
						cityName={cityName}
						cityLabel={cityLabel}
					/>
				)}

				{tab === 'reviews' && (
					<div className="foundationInner">
						<MovieCriticReviewWrapper contentId={contentId} cityName={cityName} />
					</div>
				)}

				{tab !== 'reviews' && (tab === 'about' || !frmtid || movieSessionsError) && (
					<div>
						{/*
              showAlert &&
              <div className={s.releaseDate}>
                <i className="date-upcoming">
                  <span className="path1"></span>
                  <span className="path2"></span>
                  <span className="path3"></span>
                  <span className="path4"></span>
                  <span className="path5"></span>
                </i>
                <h3>{dayjs(movieDetails.releaseDate).format('DD MMM'+"'"+'YY')}
                <small>Release Date</small></h3>
              </div>
            */}
						{/* <MovieSentiment/> */}
						<MovieDetail
							isUpcoming={showAlert}
							contentId={contentId}
							movieContent={movieDetails}
							cityName={cityName}
							cityLabel={cityLabel}
							showLanguageSelector={showLanguageSelector}
						/>
					</div>
				)}
			</div>
			{showLanguageSelector && (
				<LanguageSelectionDialog
					onCloseHandler={closeLanguageSelector}
					replace={true}
					mdpUrl={getMovieDetailUrl(movieDetails?.name, contentId)}
					movie={movieDetails}
				/>
			)}
		</>
	);
}

export default MDPContainer;
